import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Eye, Mail, MessageCircle, Package, Phone, UserRound } from "lucide-react";
import Navigation from "../components/navigation";
import { fetchSellerContactRequests } from "../api/contactRequests";
import { getMarketplaceUser, logoutMarketplace } from "../api/marketplaceAuth";
import { fetchSellerCars } from "../api/cars";
import { fetchSellerSpares } from "../api/spares";
import { fetchSellerAccessories } from "../api/accessories";

export default function SellerPortal() {
  const [requests, setRequests] = useState([]);
  const [error, setError] = useState("");
  const [listings, setListings] = useState([]);
  const user = getMarketplaceUser();

  useEffect(() => {
    Promise.all([fetchSellerContactRequests(), fetchSellerCars(), fetchSellerSpares(), fetchSellerAccessories()])
      .then(([messages, cars, spares, accessories]) => {
        setRequests(messages);
        setListings([
          ...cars.map((listing) => ({ ...listing, listingName: `${listing.make} ${listing.model}`, listingType: "Vehicle" })),
          ...spares.map((listing) => ({ ...listing, listingName: listing.name, listingType: "Spare part" })),
          ...accessories.map((listing) => ({ ...listing, listingName: listing.title, listingType: "Accessory" })),
        ]);
      })
      .catch((requestError) => setError(requestError.message));
  }, []);

  return (
    <>
      <Navigation />
      <main className="min-h-[70vh] bg-background px-4 py-10 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-5xl">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#c1392a]">Seller portal</p>
              <h1 className="mt-2 font-heading text-3xl font-bold text-dark">Messages from buyers</h1>
              <p className="mt-2 text-sm text-muted">{user?.yardName || "Your marketplace inbox"}</p>
            </div>
            <div className="flex items-center gap-3">
              <Link to="/add-listing" className="rounded-lg border border-border bg-white px-4 py-2 text-sm font-semibold text-dark">Add listing</Link>
              <button type="button" onClick={logoutMarketplace} className="text-sm font-semibold text-[#c1392a]">Sign out</button>
            </div>
          </div>
          <section className="mt-10">
            <div className="flex items-center justify-between"><h2 className="font-heading text-2xl font-bold text-dark">Your listings</h2><span className="text-sm text-muted">{listings.length} total</span></div>
            {listings.length === 0 && !error && <p className="mt-4 rounded-2xl border border-border bg-white p-6 text-sm text-muted">You have not added any listings yet.</p>}
            <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {listings.map((listing) => (
                <Link key={`${listing.listingType}-${listing.id}`} to={listing.listingType === "Vehicle" ? `/inventory/${listing.id}` : listing.listingType === "Accessory" ? `/accessories/item/${listing.id}` : `/parts/item/${listing.id}`} className="rounded-2xl border border-border bg-white p-4 shadow-sm transition-shadow hover:shadow-md">
                  <div className="flex items-start justify-between gap-3"><div><p className="text-xs font-bold uppercase tracking-wide text-[#c1392a]">{listing.listingType}</p><h3 className="mt-1 font-heading text-lg font-bold text-dark">{listing.listingName}</h3></div><Package className="h-5 w-5 shrink-0 text-muted" /></div>
                  <p className="mt-4 flex items-center gap-2 text-sm text-muted"><Eye className="h-4 w-4 text-[#c1392a]" />{listing.viewCount || 0} views</p>
                </Link>
              ))}
            </div>
          </section>
          {error && <p role="alert" className="mt-6 rounded-lg bg-red-50 p-4 text-sm text-red-700">{error}</p>}
          {!error && requests.length === 0 && <div className="mt-8 rounded-2xl border border-border bg-white p-12 text-center"><MessageCircle className="mx-auto h-8 w-8 text-[#c1392a]" /><h2 className="mt-4 font-heading text-xl font-bold text-dark">No buyer messages yet</h2><p className="mt-2 text-sm text-muted">Messages sent from your listings will appear here.</p></div>}
          <div className="mt-8 grid gap-4">
            {requests.map((request) => (
              <article key={request.id} className="rounded-2xl border border-border bg-white p-5 shadow-sm sm:p-6">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div><p className="flex items-center gap-2 font-semibold text-dark"><UserRound className="h-4 w-4 text-[#c1392a]" />{request.fullName}</p><p className="mt-1 text-xs text-muted">{new Date(request.createdAt).toLocaleString()}</p></div>
                  <span className="rounded-full bg-[#fff1e4] px-3 py-1 text-xs font-bold text-[#9a4b00]">{request.status}</span>
                </div>
                <p className="mt-5 rounded-xl bg-background p-4 text-sm leading-6 text-dark">{request.message}</p>
                <div className="mt-4 flex flex-wrap gap-4 border-t border-border pt-4 text-sm">
                  <a href={`tel:${request.phone}`} className="inline-flex items-center gap-2 font-semibold text-[#c1392a]"><Phone className="h-4 w-4" />{request.phone}</a>
                  <a href={`mailto:${request.buyer?.email || ""}`} className="inline-flex items-center gap-2 font-semibold text-[#c1392a]"><Mail className="h-4 w-4" />{request.buyer?.email || "Email unavailable"}</a>
                </div>
              </article>
            ))}
          </div>
        </div>
      </main>
    </>
  );
}
